import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Menu,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useSchool } from "../../Hooks/SchoolContext";
import { useContentSetting } from "../../Hooks/ContentSetting";
import {
  get_group_sessions_api, create_group_session_api, update_group_session_api,
  cancel_group_session_api, set_group_attendee_state_api, get_bookable_instructors_api,
} from "../../DAL/SchoolPortal/SchoolPortal";
import {
  border, textPrimary, textSecondary, textMuted, accent, font, easing,
  success, warning, error as errorColor,
  inputSx, primaryButtonSx, secondaryButtonSx, dialogPaperSx,
  formatSubjectName, subjectAccentColor,
} from "../../layouts/schoolPortal/schoolPortalTheme";
import { openSessionRoom } from "../../utils/sessionRoom";

/**
 * Running the school's group classes.
 *
 * WHAT THIS REPLACES: NOTHING. There was no screen, and no server behind one —
 * the only group-session routes were the student's join/leave/list, and the two
 * classes in production were written by a seed script. The model has carried a
 * `cancelled` status and a waitlist since it was built and nothing anywhere
 * could set either.
 *
 * The failure that made this urgent is unglamorous: a school that has to call
 * off Thursday's class could not, so eight students kept a booking for a class
 * that was not happening. Since the lesson gate shipped it is worse — a session
 * cancelled by phone and left standing keeps the student in `session_booked`,
 * and the product believes they are sorted while their next lesson stays locked.
 *
 * ONE JOB: see every class, and change the one that needs changing. Not a
 * calendar — the diary already exists and this is not it. A list, newest first,
 * where a full class and a past class are both visible precisely because the
 * student-facing list hides them and they are the two a school acts on.
 */

const money = (n, c) =>
  new Intl.NumberFormat(c === "GBP" ? "en-GB" : "en-US",
    { style: "currency", currency: c || "USD", maximumFractionDigits: 0 }).format(n || 0);

const STATE = {
  booked:      { label: "Booked",     colour: success },
  waitlisted:  { label: "Waitlisted", colour: warning },
  attended:    { label: "Attended",   colour: success },
  no_show:     { label: "No show",    colour: errorColor },
  cancelled:   { label: "Cancelled",  colour: textMuted },
};

function Pill({ text, colour, bg }) {
  return (
    <Box component="span" sx={{
      display: "inline-block", px: 0.9, py: "2px", borderRadius: "999px",
      backgroundColor: bg || `${colour}14`, border: `1px solid ${colour}44`,
    }}>
      <Typography component="span" sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 800, color: colour }}>
        {text}
      </Typography>
    </Box>
  );
}

/** One attendee, and everything that can be done to their seat. */
function AttendeeRow({ a, sessionId, disabled, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);
  const spec = STATE[a.state] || STATE.booked;

  const set = async (state) => {
    setAnchor(null);
    setBusy(true);
    try {
      const r = await set_group_attendee_state_api(sessionId, a.id, state);
      enqueueSnackbar(r.message || "Updated", { variant: r.code === 200 ? "success" : "error" });
      if (r.code === 200) onChanged();
    } finally { setBusy(false); }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.6, borderTop: `1px solid ${border}` }}>
      <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {a.name}
      </Typography>
      {/* Where the money for THIS seat is. Per attendee because the model keeps
          it per attendee — one declined card must not read as everyone's. */}
      <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: a.paymentStatus === "paid" ? success : a.paymentStatus === "failed" ? errorColor : textMuted, flexShrink: 0 }}>
        {a.paymentStatus}
      </Typography>
      <Pill text={spec.label} colour={spec.colour} />
      <Button
        size="small" disabled={disabled || busy} onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ minWidth: 0, p: 0.4, color: textMuted }}
      >{busy ? <CircularProgress size={13} /> : <MoreHorizIcon sx={{ fontSize: 17 }} />}</Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {["booked", "waitlisted", "attended", "no_show", "cancelled"]
          .filter((s) => s !== a.state)
          .map((s) => (
            <MenuItem key={s} onClick={() => set(s)} sx={{ fontFamily: font, fontSize: "13px" }}>
              {s === "cancelled" ? "Remove from the class" : `Mark ${STATE[s].label.toLowerCase()}`}
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
}

function SessionCard({ s, onChanged, onEdit, onCancel, onJoin }) {
  const [open, setOpen] = useState(false);
  const past = new Date(s.at) < new Date();
  const dead = s.status === "cancelled";
  /**
   * Roughly the server's window, so the button appears when it will work.
   * The server is the authority — it refuses to mint a token outside its own
   * window and the modal renders the countdown — but a button that is present
   * and refuses is worse than one that arrives when it is useful.
   */
  const soon = !dead && new Date(s.at).getTime() - Date.now() < 30 * 60 * 1000
    && Date.now() < new Date(s.endsAt || s.at).getTime() + 30 * 60 * 1000;
  const full = s.seatsLeft === 0;
  const unmarked = past && !dead && s.attendees.some((a) => a.state === "booked");

  return (
    <Box sx={{
      borderBottom: `1px solid ${border}`,
      opacity: dead ? 0.6 : 1,
      "&:last-of-type": { borderBottom: "none" },
    }}>
      {/* A ROW, NOT A CARD.
          This was a gradient panel with the subject's artwork bled into it, one
          per class, stacked. Two things were wrong with that. Every class this
          list holds is ALREADY in the Coming up table further up the same page,
          so the artwork was a second, louder rendering of rows the reader had
          just gone past — and the loudest thing on the page was the duplicate.
          And a full-bleed gradient per class makes six classes read as six
          unrelated objects when they are six rows of one list.

          The colour survives as an edge, which is all it was ever doing:
          saying which subject at a glance. Same treatment as the sessions
          table, so the two lists read as the same kind of thing. */}
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "18px minmax(0,1fr) auto", md: "18px 128px minmax(0,1.2fr) minmax(0,1fr) auto" },
          gap: 1.25, alignItems: "center",
          px: 1.5, py: 1.05, cursor: "pointer", userSelect: "none",
          borderLeft: `3px solid ${dead ? border : subjectAccentColor(s.subject)}`,
          transition: `background-color 0.12s ${easing}`,
          "&:hover": { backgroundColor: "#FAF8F5" },
        }}
      >
        <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted }}>
          {open ? "▾" : "▸"}
        </Typography>

        <Typography sx={{
          fontFamily: font, fontSize: "12.5px", color: textSecondary,
          fontVariantNumeric: "tabular-nums", display: { xs: "none", md: "block" },
        }}>
          {moment(s.at).format("ddd D MMM, HH:mm")}
        </Typography>

        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>
            {s.title}
          </Typography>
          <Typography noWrap sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mt: 0.1 }}>
            {/* On mobile the date column is gone, so it folds in here rather
                than disappearing. */}
            <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
              {moment(s.at).format("ddd D MMM, HH:mm")} ·{" "}
            </Box>
            Lesson {s.lessonNumber}
            {s.subject ? ` · ${formatSubjectName(s.subject)}` : ""}
            {s.instructor ? ` · ${s.instructor}` : ""}
          </Typography>
        </Box>

        <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.6, flexWrap: "wrap", minWidth: 0 }}>
          {dead && <Pill text="CANCELLED" colour={textSecondary} />}
          {!dead && full && <Pill text="FULL" colour={accent} />}
          {/* A past class with people still marked "booked" is the one thing on
              this screen nobody will think to look for. */}
          {unmarked && <Pill text="NEEDS MARKING" colour={warning} />}
        </Box>

        <Typography sx={{
          fontFamily: font, fontSize: "12px", fontWeight: 800, color: textPrimary,
          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", textAlign: "right",
        }}>
          {s.going}/{s.capacity}{s.waitlisted ? ` · ${s.waitlisted}w` : ""} · {money(s.valueTotal, s.currency)}
        </Typography>
      </Box>

      {open && (
        <Box sx={{ px: 1.25, pb: 1.25, pt: 0.5 }}>
          {s.attendees.length === 0 && (
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted, py: 0.5 }}>
              Nobody has joined yet.
            </Typography>
          )}
          {s.attendees.map((a) => (
            <AttendeeRow key={a.id} a={a} sessionId={s.id} disabled={dead} onChanged={onChanged} />
          ))}

          <Box sx={{ display: "flex", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
            {/* Until this existed a class could be scheduled, filled and paid
                for with no way to attend it — models/group_session.js has
                carried video_room_url since it was written and nothing ever
                set it. */}
            {soon && (
              <Button
                onClick={() => onJoin(s)}
                sx={{ ...primaryButtonSx, minHeight: 0, py: 0.6, px: 1.8, fontSize: "12.5px" }}
              >
                Join the class
              </Button>
            )}
            {/* Only offered when the parent passes the handler — it passes none to
                anyone the server would refuse, rather than a button that 403s. */}
            {!dead && (
              <>
                {onEdit && (
                  <Button onClick={() => onEdit(s)} sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.6, px: 1.6, fontSize: "12.5px" }}>
                    Change time or capacity
                  </Button>
                )}
                {onCancel && (
                  <Button
                    onClick={() => onCancel(s)}
                    sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.6, px: 1.6, fontSize: "12.5px", color: errorColor, borderColor: `${errorColor}55` }}
                  >
                    Cancel the class
                  </Button>
                )}
              </>
            )}
            {dead && (
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary }}>
                Cancelled. Everyone was told and their seats were settled.
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

/**
 * `embedded` renders this as a SECTION of Teaching rather than a page of its
 * own. Group classes stopped being their own nav item once the instructor
 * became the person who accepts a class rather than the person a class is
 * scheduled onto — see US_PART61_PLATFORM_SPEC.md §6.4. The screen itself did
 * not need rewriting for that, only un-framing: same list, same actions, one
 * page in.
 */
export default function GroupSessions({ embedded = false, onlyInstructorId = null }) {
  const { enqueueSnackbar } = useSnackbar();
  const { isIndependentInstructor } = useSchool();
  const { userInfo } = useContentSetting();
  // Mirrors canManageTutorsSettings on the server, which refuses create, edit
  // and cancel for anyone else. The button checked only that an instructor
  // existed, so a CFI could fill in the whole form and be turned away on
  // submit — the one failure SessionComposer says a form must never have.
  // Inlined rather than imported from TutorsShell, which sits above this file.
  const canSchedule = ["primary_admin", "staff_admin"].includes(userInfo?.role);
  const [state, setState] = useState({ loading: true, sessions: [], error: null });
  const [instructors, setInstructors] = useState([]);
  const [form, setForm] = useState(null);           // create / edit
  const [killing, setKilling] = useState(null);     // cancel confirmation
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const ListHead = () => (
    <Box sx={{
      display: { xs: "none", md: "grid" },
      gridTemplateColumns: "18px 128px minmax(0,1.2fr) minmax(0,1fr) auto",
      gap: 1.25, px: 1.5, pb: 0.6, pl: "calc(12px + 3px)",
      borderBottom: `1px solid ${border}`,
    }}>
      {["", "When", "Class", "", "Seats · value"].map((h, i) => (
        <Typography key={i} sx={{
          fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.06em",
          textTransform: "uppercase", color: textMuted,
          textAlign: i === 4 ? "right" : "left",
        }}>{h}</Typography>
      ))}
    </Box>
  );

  const load = useCallback(async () => {
    const r = await get_group_sessions_api();
    if (r.code === 200) setState({ loading: false, sessions: r.sessions || [], error: null });
    else setState({ loading: false, sessions: [], error: r.message || "Could not load your group sessions" });
  }, []);

  useEffect(() => {
    load();
    get_bookable_instructors_api().then((r) => r.code === 200 && setInstructors(r.instructors || []));
  }, [load]);

  /**
   * `onlyInstructorId` narrows the list to one instructor's own classes.
   *
   * GET /api/school/group-sessions is school-scoped by design — the front desk
   * has to see every class to call one off. Unscoped on a teacher's own page
   * that means a CFI can cancel a colleague's Thursday evening, refunding
   * eight seats that were nothing to do with them. You can only cancel what
   * you can see, so scoping the LIST is the whole control.
   */
  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const mine = onlyInstructorId
      ? state.sessions.filter((s) => String(s.instructorId) === String(onlyInstructorId))
      : state.sessions;
    return {
      upcoming: mine.filter((s) => new Date(s.at).getTime() >= now),
      past: mine.filter((s) => new Date(s.at).getTime() < now),
    };
  }, [state.sessions, onlyInstructorId]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        instructorId: form.instructorId,
        lessonNumber: Number(form.lessonNumber),
        title: form.title,
        subject: form.subject || "",
        start_time: new Date(form.start).toISOString(),
        end_time: new Date(form.end).toISOString(),
        capacity: Number(form.capacity),
        pricePerHead: Number(form.pricePerHead),
        // Only on create. Repeating an EXISTING class would have to decide
        // whether it edits this one or all of them, and for a class people have
        // already booked onto the answer is always "this one".
        ...(form.id ? {} : { repeatWeeks: Number(form.repeatWeeks) || 1 }),
      };
      const r = form.id
        ? await update_group_session_api(form.id, payload)
        : await create_group_session_api(payload);
      enqueueSnackbar(r.message || "Saved", { variant: r.code === 200 ? "success" : "error" });
      if (r.code === 200) { setForm(null); load(); }
    } finally { setBusy(false); }
  };

  const kill = async () => {
    setBusy(true);
    try {
      const r = await cancel_group_session_api(killing.id, reason);
      enqueueSnackbar(r.message || "Cancelled", { variant: r.code === 200 ? "success" : "error" });
      if (r.code === 200) { setKilling(null); setReason(""); load(); }
    } finally { setBusy(false); }
  };

  const blank = () => ({
    id: null, instructorId: onlyInstructorId || instructors[0]?.id || "", lessonNumber: 10, title: "",
    subject: "", capacity: 8, pricePerHead: 25, repeatWeeks: 1,
    start: moment().add(7, "days").hour(18).minute(0).format("YYYY-MM-DDTHH:mm"),
    end: moment().add(7, "days").hour(19).minute(0).format("YYYY-MM-DDTHH:mm"),
  });

  if (state.loading) {
    return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={22} sx={{ color: accent }} /></Box>;
  }

  return (
    <Box sx={embedded ? {} : { maxWidth: 900, mx: "auto", px: 2, py: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!embedded && (
            <Typography sx={{ fontFamily: font, fontSize: "20px", fontWeight: 800, color: textPrimary }}>
              Group classes
            </Typography>
          )}
          <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary, mt: 0.3, lineHeight: 1.5 }}>
            {isIndependentInstructor
              ? "Four lessons are discussion-shaped. A class sells several seats against a single hour of your time — the only thing you offer that earns more the more people turn up."
              : "Four lessons are discussion-shaped. A class fills seats against one instructor hour, which is the only touchpoint whose margin improves with attendance."}
          </Typography>
        </Box>
        {canSchedule && (
          <Button
            startIcon={<AddIcon sx={{ fontSize: 17 }} />}
            onClick={() => setForm(blank())}
            disabled={!instructors.length}
            sx={{ ...primaryButtonSx, flexShrink: 0, minHeight: 0, py: 0.9, px: 2, fontSize: "13px" }}
          >
            Schedule a class
          </Button>
        )}
      </Box>

      {state.error && (
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: errorColor, mb: 2 }}>{state.error}</Typography>
      )}
      {!instructors.length && (
        <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: warning, mb: 2, lineHeight: 1.5 }}>
          {isIndependentInstructor
            ? "You are not set up to teach yet, so there is nobody to run a class. Turn on bookings under My Teaching first."
            : "Nobody at your school is set up to teach yet, so there is nobody to run a class. Add an instructor under Staff first."}
        </Typography>
      )}

      {!state.sessions.length && !state.error && (
        <Box sx={{ py: 4 }}>
          <Typography sx={{ fontFamily: font, fontSize: "14px", fontWeight: 600, color: textPrimary }}>
            No group classes yet.
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mt: 0.4, lineHeight: 1.5 }}>
            Lessons 6, 10 and 11 are the ones built for it — airport and radio work, weather theory, and
            weather products. Radio phraseology in particular needs other voices; practising it alone teaches
            you to say the words, not to hear them.
          </Typography>
        </Box>
      )}

      {upcoming.length > 0 && (
        <>
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: textSecondary, mb: 0.75 }}>
            Coming up
          </Typography>
          <ListHead />
          {upcoming.map((s) => (
            <SessionCard key={s.id} s={s} onChanged={load} onCancel={canSchedule ? setKilling : null} onJoin={(s) => openSessionRoom(s.id, { group: true })}
              onEdit={canSchedule ? (x) => setForm({
                id: x.id, instructorId: String(x.instructorId), lessonNumber: x.lessonNumber,
                title: x.title, subject: x.subject || "", capacity: x.capacity, pricePerHead: x.pricePerHead,
                start: moment(x.at).format("YYYY-MM-DDTHH:mm"),
                end: moment(x.endsAt).format("YYYY-MM-DDTHH:mm"),
              }) : null} />
          ))}
        </>
      )}

      {past.length > 0 && (
        <>
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: textSecondary, mt: 2.5, mb: 0.75 }}>
            Already run
          </Typography>
          <ListHead />
          {past.map((s) => (
            <SessionCard key={s.id} s={s} onChanged={load} onCancel={canSchedule ? setKilling : null} onJoin={(s) => openSessionRoom(s.id, { group: true })} onEdit={null} />
          ))}
        </>
      )}

      {/* ── create / edit ─────────────────────────────────────────────── */}
      <Dialog open={Boolean(form)} onClose={() => setForm(null)} PaperProps={{ sx: dialogPaperSx }} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: font, fontSize: "15px", fontWeight: 800 }}>
          {form?.id ? "Change this class" : "Schedule a group class"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {instructors.length > 1 ? (
            <TextField select size="small" fullWidth label="Instructor" value={form?.instructorId || ""}
              onChange={(e) => setForm({ ...form, instructorId: e.target.value })} sx={{ ...inputSx, mb: 1.5 }}>
              {instructors.map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
            </TextField>
          ) : null}
          <TextField size="small" fullWidth type="number" label="Lesson" value={form?.lessonNumber ?? ""}
            onChange={(e) => setForm({ ...form, lessonNumber: e.target.value })} sx={{ ...inputSx, mb: 1.5 }}
            helperText="The syllabus lesson it covers — 6, 10 and 11 are the discussion-shaped ones." />
          <TextField size="small" fullWidth label="Title" value={form?.title || ""}
            onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ ...inputSx, mb: 1.5 }}
            placeholder="Leave blank to use the lesson's own title" />
          <TextField size="small" fullWidth label="Subject key" value={form?.subject || ""}
            onChange={(e) => setForm({ ...form, subject: e.target.value })} sx={{ ...inputSx, mb: 1.5 }}
            helperText="Optional, and checked against the topic tree — it is what colours the class." />
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <TextField size="small" fullWidth type="datetime-local" label="Starts" InputLabelProps={{ shrink: true }}
              value={form?.start || ""} onChange={(e) => setForm({ ...form, start: e.target.value })} sx={inputSx} />
            <TextField size="small" fullWidth type="datetime-local" label="Ends" InputLabelProps={{ shrink: true }}
              value={form?.end || ""} onChange={(e) => setForm({ ...form, end: e.target.value })} sx={inputSx} />
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField size="small" fullWidth type="number" label="Capacity" value={form?.capacity ?? ""}
              onChange={(e) => setForm({ ...form, capacity: e.target.value })} sx={inputSx} />
            <TextField size="small" fullWidth type="number" label="Price per head" value={form?.pricePerHead ?? ""}
              onChange={(e) => setForm({ ...form, pricePerHead: e.target.value })} sx={inputSx} />
          </Box>
          {!form?.id && (
            <TextField
              select size="small" fullWidth label="Repeat" value={form?.repeatWeeks ?? 1}
              onChange={(e) => setForm({ ...form, repeatWeeks: e.target.value })} sx={{ ...inputSx, mt: 1.5 }}
              helperText={
                Number(form?.repeatWeeks) > 1
                  ? "Writes real classes, one a week. Each can be moved, filled or cancelled on its own — a week the instructor is already busy is skipped."
                  : "A school running the same class every Tuesday was otherwise filling this form six times."
              }
            >
              <MenuItem value={1}>Just this once</MenuItem>
              {[2, 4, 6, 8, 12].map((n) => <MenuItem key={n} value={n}>Weekly, {n} weeks</MenuItem>)}
            </TextField>
          )}
          {form?.id && (
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mt: 1.5, lineHeight: 1.45 }}>
              Changing the price moves what the next person pays. It does not re-charge or refund anyone who
              has already booked. Moving the time tells everyone holding a seat.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setForm(null)} sx={secondaryButtonSx}>Cancel</Button>
          <Button onClick={save} disabled={busy} sx={primaryButtonSx}>{busy ? "Saving…" : "Save"}</Button>
        </DialogActions>
      </Dialog>

      {/* ── cancel ────────────────────────────────────────────────────── */}
      <Dialog open={Boolean(killing)} onClose={() => setKilling(null)} PaperProps={{ sx: dialogPaperSx }} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: font, fontSize: "15px", fontWeight: 800 }}>
          Cancel {killing?.title}?
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {/* Says what will actually happen to the money before it happens. A
              cancel button that does not tell you it is issuing seven refunds is
              a button people press once and then never trust. */}
          <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary, mb: 1.5, lineHeight: 1.55 }}>
            {killing?.going
              ? <>
                  <strong>{killing.going} student{killing.going === 1 ? "" : "s"}</strong> will be told, and every
                  seat settled: a paid card is refunded and an uncaptured hold is released. That is {money(killing.valueTotal, killing.currency)} of
                  seats.
                </>
              : "Nobody has joined this class, so there is nothing to refund."}
          </Typography>
          <TextField size="small" fullWidth multiline minRows={2} label="Why (they will see this)"
            value={reason} onChange={(e) => setReason(e.target.value)} sx={inputSx}
            placeholder="Instructor unwell — we will put another date up this week." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setKilling(null)} sx={secondaryButtonSx}>Keep it</Button>
          <Button onClick={kill} disabled={busy} sx={{ ...primaryButtonSx, backgroundColor: errorColor }}>
            {busy ? "Cancelling…" : "Cancel the class"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
